import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  Typography
} from '@esgian/esgianui';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { useSelector } from 'react-redux';
import { getSessionStatus, getLookupRigs } from '@store/features';
import { APP_NAME, DATE_FORMAT } from '@constants/index';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@hooks';
import { useTimezone } from '@hooks/useTimezone';
import {
  getMyReportRequests,
  setReportActiveStatus,
  archiveReport,
  sendReportNow,
  sendAndDownloadReport
} from '@api/ReportScheduler';
import CreateReportModal from '../../components/Modals/ReportScheduler/CreateReportModal';
import ConfirmModal from '../../components/Modals/ReportScheduler/ConfirmModal';
import HistoryDetailsView from '../../components/SubSections/ReportScheduler/HistoryDetailsView';
import ExecutionsTab from '../../components/Sections/ReportScheduler/ExecutionsTab';
import ArchivedTab from '../../components/Sections/ReportScheduler/ArchivedTab';
import moment from 'moment-timezone';
import { logError } from '@api/Logging';

function ActionMenu({ report, onEdit, onArchive, onDuplicate, onActivate, onDeactivate }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const menuItems = [
    { label: 'Edit', handler: () => onEdit(report) },
    { label: 'Duplicate', handler: () => onDuplicate(report) }
  ];

  // Only show activate/deactivate for scheduled (non-immediate) reports
  if (report && !report.isImmediate) {
    if (report.isActive) {
      menuItems.push({ label: 'Deactivate', handler: () => onDeactivate(report.id) });
    } else {
      menuItems.push({ label: 'Activate', handler: () => onActivate(report.id) });
    }
  }

  menuItems.push({ label: 'Archive', handler: () => onArchive(report.id) });

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }} ref={menuRef}>
      <Tooltip title="Actions">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && (
        <Box
          sx={{
            position: 'fixed',
            bgcolor: 'background.default',
            borderRadius: 1,
            boxShadow: 3,
            py: 0.5,
            minWidth: 140,
            zIndex: 1300
          }}
          style={{
            top: menuRef.current?.getBoundingClientRect().bottom ?? 0,
            left: (menuRef.current?.getBoundingClientRect().right ?? 0) - 140
          }}>
          {menuItems.map(({ label, handler }) => (
            <Box
              key={label}
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                handler();
                setOpen(false);
              }}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                color: 'text.primary',
                px: 2,
                py: 1,
                cursor: 'pointer',
                fontSize: '14px',
                '&:hover': { bgcolor: 'action.hover' }
              }}>
              {label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

const PERIOD_LABELS = {
  Last24Hours: 'Last 24 Hours',
  Last7Days: 'Last 7 Days',
  Last14Days: 'Last 14 Days',
  LastMonth: 'Last Month',
  CurrentWell: 'Current Well',
  Custom: 'Custom'
};

function ReportsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, reportId: null, type: null });
  const [expandedRow, setExpandedRow] = useState([]);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [sortOrder, setSortOrder] = useState({ name: 'requestDate', direction: 'desc' });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = parseInt(localStorage.getItem('reportsRowsPerPage'), 10);
    return [10, 25, 50, 100].includes(stored) ? stored : 10;
  });

  const sessionStatus = useSelector(getSessionStatus);
  const lookupRigs = useSelector(getLookupRigs);
  const sessionReady = sessionStatus === 'ok';
  const queryClient = useQueryClient();
  const { selectedTimeZone } = useTimezone();

  const {
    theme: {
      palette: { mode }
    }
  } = useTheme();

  const myRequestsQuery = useQuery({
    queryKey: ['scheduledReport', 'myRequests'],
    enabled: sessionReady,
    placeholderData: [],
    queryFn: ({ signal }) => getMyReportRequests(signal)
  });

  const reports = myRequestsQuery.data || [];

  const reportsRef = useRef(reports);
  reportsRef.current = reports;
  const expandedReportIdRef = useRef(expandedReportId);
  expandedReportIdRef.current = expandedReportId;

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return selectedTimeZone
      ? moment.tz(dateStr, selectedTimeZone).format(DATE_FORMAT)
      : moment(dateStr).format(DATE_FORMAT);
  };

  // Collapse any open history row and restore the default "Created" sort.
  // Called after a new report is created so the newest row surfaces at the top.
  const resetTableView = useCallback(() => {
    setExpandedRow([]);
    setExpandedReportId(null);
    setSortOrder({ name: 'requestDate', direction: 'desc' });
  }, []);

  const activateControllerRef = useRef(null);
  const archiveControllerRef = useRef(null);
  useEffect(() => {
    return () => {
      activateControllerRef.current?.abort();
      archiveControllerRef.current?.abort();
    };
  }, []);

  const handleActivate = useCallback(
    async (reportId) => {
      activateControllerRef.current = new AbortController();
      await setReportActiveStatus(activateControllerRef.current.signal, reportId, true);
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
      setConfirmModal({ show: false, reportId: null, type: null });
    },
    [queryClient]
  );

  const handleDeactivate = useCallback(
    async (reportId) => {
      activateControllerRef.current = new AbortController();
      await setReportActiveStatus(activateControllerRef.current.signal, reportId, false);
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
    },
    [queryClient]
  );

  const handleArchive = useCallback(
    async (reportId) => {
      archiveControllerRef.current = new AbortController();
      await archiveReport(archiveControllerRef.current.signal, reportId);
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'archived'] });
      setConfirmModal({ show: false, reportId: null, type: null });
    },
    [queryClient]
  );

  const handleEdit = useCallback((report) => {
    setEditingReport(report);
    setShowCreateModal(true);
  }, []);

  const handleDuplicate = useCallback((report) => {
    setEditingReport({
      ...report,
      id: undefined,
      isImmediate: false,
      reportName: `${report.reportName || ''} (Copy)`
    });
    setShowCreateModal(true);
  }, []);

  const handleArchiveConfirm = useCallback((reportId) => {
    setConfirmModal({ show: true, reportId, type: 'archive' });
  }, []);

  const handleSendNow = useCallback(
    (body) => {
      resetTableView();
      if (body.downloadImmediate) {
        // Optimistic: create placeholder item for requests table
        const optimisticRequestId = `optimistic-${Date.now()}`;

        const optimisticRequest = {
          id: optimisticRequestId,
          reportName: body.reportName || 'Generating...',
          rigName: body.rigName || '',
          reportType: body.reportType,
          frequency: 'Immediate',
          period: body.period,
          emails: body.emails || [],
          requestDate: moment().toISOString(),
          isActive: true,
          isImmediate: true,
          _optimistic: true
        };

        queryClient.setQueryData(['scheduledReport', 'myRequests'], (old) => [
          optimisticRequest,
          ...(old || [])
        ]);

        sendAndDownloadReport(null, body)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
            queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'executions'] });
            queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'history'] });
          })
          .catch((error) => {
            logError(error, 'Failed to send and download report');
            queryClient.setQueryData(['scheduledReport', 'myRequests'], (old) =>
              (old || []).filter((request) => request.id !== optimisticRequestId)
            );
          });
      } else {
        sendReportNow(null, body)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
            queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'executions'] });
            queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'history'] });
          })
          .catch((error) => {
            logError(error, 'Failed to send report');
          });
      }
    },
    [queryClient, resetTableView]
  );

  const toggleExpandRow = useCallback(
    (reportId) => {
      if (expandedReportIdRef.current === reportId) {
        setExpandedRow([]);
        setExpandedReportId(null);
      } else {
        const dataIndex = reportsRef.current.findIndex((r) => r.id === reportId);
        setExpandedRow(dataIndex >= 0 ? [dataIndex] : []);
        setExpandedReportId(reportId);
      }
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        name: 'reportName',
        label: 'Title',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => value || '--'
        }
      },
      {
        name: 'requestDate',
        label: 'Created',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => formatDate(value),
          sortCompare: (order) => (a, b) => {
            const aVal = a.data ? moment(a.data).valueOf() : 0;
            const bVal = b.data ? moment(b.data).valueOf() : 0;
            return (aVal - bVal) * (order === 'asc' ? 1 : -1);
          }
        }
      },
      {
        name: 'rigName',
        label: 'Content',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value, tableMeta) => {
            const report = reports.find((r) => r.id === tableMeta.rowData[6]);
            if (report?.isImmediate) {
              const parts = [report?.rigName, report?.reportType].filter(Boolean);
              return (
                <Box>
                  <Typography variant="body2">{parts.join(', ') || '--'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sent immediately
                  </Typography>
                </Box>
              );
            }
            const parts = [report?.rigName, report?.period, report?.reportType].filter(Boolean);
            return (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {parts.join(', ') || '--'}
              </Typography>
            );
          }
        }
      },
      {
        name: 'frequency',
        label: 'Schedule detail',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value, tableMeta) => {
            const report = reports.find((r) => r.id === tableMeta.rowData[6]);
            if (report?.isImmediate) {
              const periodLabel = PERIOD_LABELS[report?.period] || report?.period || '--';
              return (
                <Box>
                  <Typography variant="body2">Immediate</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {periodLabel}
                  </Typography>
                </Box>
              );
            }
            if (!value) return '--';
            const periodLabel = PERIOD_LABELS[report?.period] || report?.period || '';
            return (
              <Box>
                <Typography variant="body2">{`Send out ${value.toLowerCase()}`}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`From ${formatDate(report?.startDate)}`}
                  {periodLabel ? ` · ${periodLabel}` : ''}
                </Typography>
              </Box>
            );
          }
        }
      },
      {
        name: 'emails',
        label: 'Recipients',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => {
            if (!Array.isArray(value) || value.length === 0) return '--';
            return <Typography variant="body2">{value.join(', ')}</Typography>;
          },
          sortCompare: (order) => (a, b) => {
            const aVal = Array.isArray(a.data) ? a.data.join(', ') : '';
            const bVal = Array.isArray(b.data) ? b.data.join(', ') : '';
            return aVal.localeCompare(bVal) * (order === 'asc' ? 1 : -1);
          }
        }
      },
      {
        name: 'isActive',
        label: 'Status',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value, tableMeta) => {
            const report = reports.find((r) => r.id === tableMeta.rowData[6]);

            if (report?._optimistic) {
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} />
                </Stack>
              );
            }

            if (report?.isImmediate) {
              return (
                <Chip label="Immediate" size="small" color="info" variant="outlined" />
              );
            }

            if (!value) {
              return (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmModal({ show: true, reportId: report?.id, type: 'activate' });
                  }}
                  sx={{
                    textTransform: 'uppercase',
                    borderRadius: '100px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap'
                  }}>
                  Activate it
                </Button>
              );
            }
            return <Chip label="Active" size="small" color="success" variant="outlined" />;
          },
          sortCompare: (order) => (a, b) => {
            const aVal = a.data ? 1 : 0;
            const bVal = b.data ? 1 : 0;
            return (aVal - bVal) * (order === 'asc' ? 1 : -1);
          }
        }
      },
      {
        name: 'id',
        label: 'Action',
        options: {
          sort: false,
          filter: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const report = reports.find((r) => r.id === tableMeta.rowData[6]);
            const isExpanded = expandedReportId === report?.id;
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Button
                  disabled={!!report?._optimistic}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandRow(report?.id);
                  }}
                  endIcon={
                    isExpanded ? (
                      <ArrowUpwardRoundedIcon />
                    ) : (
                      <ArrowDownwardRoundedIcon />
                    )
                  }
                  size="small"
                  variant="text">
                  <Typography variant="caption">History</Typography>
                </Button>
                {!report?._optimistic && (
                  <ActionMenu
                    report={report}
                    onEdit={handleEdit}
                    onArchive={handleArchiveConfirm}
                    onDuplicate={handleDuplicate}
                    onActivate={(id) =>
                      setConfirmModal({ show: true, reportId: id, type: 'activate' })
                    }
                    onDeactivate={handleDeactivate}
                  />
                )}
              </Stack>
            );
          }
        }
      }
    ],
    [expandedReportId, reports, handleEdit, handleArchiveConfirm, handleDuplicate, handleDeactivate, toggleExpandRow]
  );

  const options = useMemo(
    () => ({
      selectableRows: 'none',
      responsive: 'standard',
      search: true,
      download: false,
      print: false,
      viewColumns: false,
      filter: false,
      pagination: true,
      elevation: 0,
      toolbar: true,
      rowsPerPageOptions: [10, 25, 50, 100],
      rowsPerPage,
      onChangeRowsPerPage: (numberOfRows) => {
        setRowsPerPage(numberOfRows);
        localStorage.setItem('reportsRowsPerPage', String(numberOfRows));
      },
      sortOrder,
      onColumnSortChange: (changedColumn, direction) =>
        setSortOrder({ name: changedColumn, direction }),
      expandableRows: true,
      expandableRowsHeader: false,
      expandableRowsOnClick: false,
      rowsExpanded: expandedRow,
      renderExpandableRow: (rowData, rowMeta) => {
        const numColumns = rowData.length + 1;
        const report = reports[rowMeta.dataIndex];
        return <HistoryDetailsView reportId={report?.id} numColumns={numColumns} />;
      },
      customToolbar: () => (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingReport(null);
            setShowCreateModal(true);
          }}
          sx={{ textTransform: 'uppercase', borderRadius: '100px', ml: 1 }}>
          Create a report
        </Button>
      )
    }),
    [expandedRow, reports, sortOrder, rowsPerPage]
  );

  return (
    <Box sx={{ minHeight: '100vh', width: '100vw' }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              aria-label="reports tabs"
              sx={{
                '& .MuiTab-root': { textTransform: 'none' }
              }}>
              <Tab label="Reports" />
              <Tab label="Executions" />
              <Tab label="Archived" />
            </Tabs>
          </Paper>
        </Grid>

        {tabValue === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ overflow: 'visible' }}>
              <Table
                customStyle={{}}
                title="Reports"
                mode={mode}
                data={reports}
                columns={columns}
                options={options}
                app={APP_NAME}
                loading={myRequestsQuery.isFetching}
              />
            </Paper>
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid item xs={12}>
            <ExecutionsTab />
          </Grid>
        )}

        {tabValue === 2 && (
          <Grid item xs={12}>
            <ArchivedTab />
          </Grid>
        )}
      </Grid>

      {/* Create / Edit Report Modal */}
      {showCreateModal && (
        <CreateReportModal
          open={showCreateModal}
          handleClose={() => {
            setShowCreateModal(false);
            setEditingReport(null);
          }}
          lookupRigs={lookupRigs}
          editMode={!!editingReport?.id}
          initialData={editingReport}
          onSendNow={handleSendNow}
          onSaved={resetTableView}
        />
      )}

      {/* Confirm Modal (Activate / Archive) */}
      {confirmModal.show && (
        <ConfirmModal
          type={confirmModal.type}
          onConfirm={() => {
            if (confirmModal.type === 'activate') {
              handleActivate(confirmModal.reportId);
            } else if (confirmModal.type === 'archive') {
              handleArchive(confirmModal.reportId);
            }
          }}
          onCancel={() => setConfirmModal({ show: false, reportId: null, type: null })}
        />
      )}
    </Box>
  );
}

ReportsPage.propTypes = {};
ReportsPage.defaultProps = {};

export default ReportsPage;
